<div class="mx-auto w-[85%] px-4 py-8 sm:px-6 lg:px-8">
  <div class="mb-8">
    <h1 class="text-3xl font-bold tracking-tight text-slate-900">Admin Dashboard</h1>
    <p class="mt-2 text-sm text-slate-500">Manage question banks, import data, and monitor system status.</p>
  </div>

  <div class="space-y-8">
    <!-- System Health and Default Grading Model in one row (top row) -->
    <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
      <section class="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 class="mb-4 text-lg font-semibold text-slate-800">System Health</h2>
        <a
          [href]="apiHealthUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="group flex items-center justify-between rounded-lg border border-slate-200 p-4 transition-colors hover:bg-slate-50"
        >
          <div class="flex items-center gap-3">
            <span class="relative flex h-3 w-3">
              <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
              <span class="relative inline-flex h-3 w-3 rounded-full bg-green-500"></span>
            </span>
            <span class="font-medium text-slate-700 group-hover:text-blue-600">API Status</span>
          </div>
          <svg class="h-4 w-4 text-slate-400 group-hover:text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
          </svg>
        </a>
      </section>

      <section class="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 class="mb-4 text-lg font-semibold text-slate-800">Allow Copy/Paste (Practice)</h2>
        <p class="mb-4 text-sm text-slate-600">
          When enabled, users can copy and paste text in the practice exam input area. Useful for testing. Default: disabled.
        </p>
        @if (admin.allowCopyPasteLoading()) {
          <div class="flex items-center gap-2 text-sm text-slate-500">
            <svg class="h-5 w-5 animate-spin text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading...
          </div>
        } @else if (admin.allowCopyPasteError()) {
          <div class="rounded-md bg-red-50 p-3 text-sm text-red-700">{{ admin.allowCopyPasteError() }}</div>
        } @else {
          <label class="flex cursor-pointer items-center gap-3">
            <span class="switch">
              <input
                type="checkbox"
                [checked]="admin.allowCopyPaste()"
                (change)="onAllowCopyPasteChange($any($event.target).checked)"
                [disabled]="admin.allowCopyPasteSaving()"
              />
              <span class="slider round"></span>
            </span>
            <span class="text-sm font-medium text-slate-700">Allow copy/paste in practice exam</span>
          </label>
          @if (admin.allowCopyPasteSaving()) {
            <p class="mt-2 text-xs text-slate-500">Saving...</p>
          }
        }
      </section>

      <section class="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 class="mb-4 text-lg font-semibold text-slate-800">Default Grading Model</h2>
        <p class="mb-4 text-sm text-slate-600">
          Choose the OpenAI model used for grading writing submissions. The API will use this model for all grading requests.
        </p>
        @if (admin.modelLoading()) {
          <div class="flex items-center gap-2 text-sm text-slate-500">
            <svg class="h-5 w-5 animate-spin text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading...
          </div>
        } @else if (admin.modelError()) {
          <div class="rounded-md bg-red-50 p-3 text-sm text-red-700">{{ admin.modelError() }}</div>
        } @else {
          <select
            [value]="admin.defaultModel()"
            (change)="onModelChange($any($event.target).value)"
            [disabled]="admin.modelSaving()"
            class="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            @for (m of admin.allowedModels(); track m) {
              <option [value]="m" [selected]="m === admin.defaultModel()">{{ m }}</option>
            }
          </select>
          @if (admin.modelSaving()) {
            <p class="mt-2 text-xs text-slate-500">Saving...</p>
          }
        }
      </section>
    </div>

    <!-- Question Bank Upload and Database Overview in one row -->
    <div class="flex flex-col gap-6 lg:flex-row">
      <!-- Question Bank Upload -->
      <section class="rounded-xl border border-slate-200 bg-white p-6 shadow-sm lg:w-[65%]">
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-slate-800">Question Bank Upload</h2>
          <p class="mt-1 text-sm text-slate-600">
            Upload an Excel file (.xlsx) to sync question bank data to DynamoDB. Ensure your file contains the following exact column headers:
          </p>
          <div class="mt-3 flex flex-wrap gap-2">
            <span class="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-600">yearmonth</span>
            <span class="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-600">combination</span>
            <span class="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-600">tache1</span>
            <span class="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-600">tache2</span>
            <span class="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-600">tache3Topic</span>
            <span class="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-600">tache3Document1</span>
            <span class="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-600">tache3Document2</span>
          </div>
        </div>

        <p-fileupload
          #fileUpload
          name="file"
          [customUpload]="true"
          (uploadHandler)="onCustomUpload($event)"
          accept=".xlsx,.xls"
          [maxFileSize]="5000000"
          mode="advanced"
        >
          <ng-template #header let-files let-chooseCallback="chooseCallback" let-clearCallback="clearCallback" let-uploadCallback="uploadCallback">
            <div class="flex flex-wrap gap-3 items-center">
              <button
                type="button"
                class="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-slate-800"
                (click)="triggerChoose($event, chooseCallback)"
              >
                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Choose File
              </button>
              <button
                type="button"
                class="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-200 [&_svg]:text-slate-600"
                [disabled]="!files?.length"
                (click)="uploadCallback()"
              >
                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                </svg>
                Upload
              </button>
              <button
                type="button"
                class="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-200 [&_svg]:text-slate-600"
                [disabled]="!files?.length"
                (click)="clearCallback()"
              >
                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
                Cancel
              </button>
            </div>
          </ng-template>
          <ng-template #content let-files let-removeFileCallback="removeFileCallback">
            @if (files?.length) {
              <div class="mt-4 flex flex-col gap-2">
                @for (file of files; track file.name + file.size; let i = $index) {
                  <div class="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                    <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                      <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25h-2.25a2.25 2.25 0 0 1-2.25-2.25v-2.25Z" />
                      </svg>
                    </div>
                    <div class="min-w-0 flex-1">
                      <span class="truncate text-sm font-medium text-slate-900">{{ file.name }}</span>
                      <span class="ml-2 text-sm text-slate-500">{{ formatFileSize(file.size) }}</span>
                    </div>
                    <span class="shrink-0 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">Pending</span>
                    <button
                      type="button"
                      class="shrink-0 rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600"
                      (click)="removeFile($event, i, removeFileCallback)"
                      title="Remove file"
                    >
                      <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                }
              </div>
            }
          </ng-template>
          <ng-template #empty>
            <div class="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 py-10 transition-colors hover:bg-slate-100">
              <svg class="mb-3 h-10 w-10 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
              </svg>
              <p class="text-sm font-medium text-slate-600">Drag and drop an Excel file here</p>
              <p class="mt-1 text-xs text-slate-500">or click "Choose File" to browse</p>
            </div>
          </ng-template>
        </p-fileupload>

        @if (admin.uploadLoading()) {
          <div class="mt-6 flex items-center gap-3 text-sm font-medium text-slate-600">
            <svg class="h-5 w-5 animate-spin text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            Processing and uploading data...
          </div>
        }

        @if (admin.uploadError()) {
          <div class="mt-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            <svg class="h-5 w-5 shrink-0 text-red-500" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" /></svg>
            <div>
              <h3 class="font-medium">Upload Failed</h3>
              <p class="mt-1 text-red-700">{{ admin.uploadError() }}</p>
            </div>
          </div>
        }

        @if (uploadResult(); as result) {
          <div class="mt-6 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
            <div class="flex items-start gap-3">
              <svg class="h-5 w-5 shrink-0 text-green-500" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" /></svg>
              <div>
                <p class="font-medium">Successfully imported {{ result.imported }} combination(s).</p>
                @if (result.errors.length > 0) {
                  <div class="mt-2">
                    <p class="font-medium text-red-800">However, some errors occurred:</p>
                    <ul class="mt-1 list-inside list-disc space-y-1 text-red-700">
                      @for (err of result.errors; track err) {
                        <li>{{ err }}</li>
                      }
                    </ul>
                  </div>
                }
              </div>
            </div>
          </div>
        }
      </section>

      <!-- Database Overview -->
      <section class="rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col max-h-[500px] lg:w-[35%]">
        <div class="border-b border-slate-200 p-6 pb-4">
          <h2 class="text-lg font-semibold text-slate-800">Database Overview</h2>
          <p class="mt-1 text-sm text-slate-500">Current combinations in DynamoDB.</p>
        </div>

        <div class="flex-1 overflow-y-auto p-0">
          @if (admin.summaryLoading()) {
            <div class="flex items-center justify-center py-12 text-sm text-slate-500">
              <svg class="mr-2 h-5 w-5 animate-spin text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              Loading data...
            </div>
          } @else if (admin.summaryError()) {
            <div class="m-4 rounded-md bg-red-50 p-4 text-sm text-red-700">
              {{ admin.summaryError() }}
            </div>
          } @else if (summary().length === 0) {
            <div class="py-12 text-center text-sm text-slate-500">
              No data yet. Upload an Excel file to get started.
            </div>
          } @else {
            <table class="min-w-full divide-y divide-slate-200">
              <thead class="bg-slate-50 sticky top-0">
                <tr>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Year-Month</th>
                  <th scope="col" class="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Count</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100 bg-white">
                @for (item of summary(); track item.yearmonth) {
                  <tr class="transition-colors hover:bg-slate-50">
                    <td class="whitespace-nowrap px-6 py-3 text-sm font-medium text-slate-900">{{ item.yearmonth }}</td>
                    <td class="whitespace-nowrap px-6 py-3 text-right text-sm text-slate-600">
                      <span class="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-800">
                        {{ item.count }}
                      </span>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          }
        </div>
      </section>
    </div>

    <!-- User Management -->
    <app-user-management />
  </div>
</div>